import { useState, useEffect } from 'react';
import { Settings, BookOpen, BarChart3, LogOut, Trash2, Edit2, Send, Layers, Zap } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function getAuth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('tfg_token')}` } }; }

function TabSettings() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/settings`, getAuth()).then(r => setSettings(r.data)).catch(() => {}); }, []);
  const save = async () => { setSaving(true); await axios.put(`${API}/admin/settings`, settings, getAuth()).catch(() => {}); setSaving(false); };
  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-[#1C1917]">Site Settings</h3>
      <div><label className="block text-sm font-medium mb-1">Site Name</label><input value={settings.site_name || ''} onChange={e => setSettings({...settings, site_name: e.target.value})} className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" data-testid="setting-site-name" /></div>
      <div><label className="block text-sm font-medium mb-1">Contact Email</label><input value={settings.contact_email || ''} onChange={e => setSettings({...settings, contact_email: e.target.value})} className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" data-testid="setting-email" /></div>
      <div><label className="block text-sm font-medium mb-1">Amazon Affiliate ID</label><input value={settings.amazon_affiliate_id || ''} onChange={e => setSettings({...settings, amazon_affiliate_id: e.target.value})} className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" data-testid="setting-affiliate" /></div>
      <div><label className="block text-sm font-medium mb-1">Google AdSense Publisher ID</label><input value={settings.adsense_pub_id || ''} onChange={e => setSettings({...settings, adsense_pub_id: e.target.value})} className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" placeholder="ca-pub-XXXXXXXXXX" data-testid="setting-adsense-id" /></div>
      <div className="flex items-center gap-2"><input type="checkbox" checked={settings.adsense_enabled || false} onChange={e => setSettings({...settings, adsense_enabled: e.target.checked})} data-testid="setting-adsense" /><label className="text-sm">Enable AdSense</label></div>
      <button onClick={save} disabled={saving} className="px-6 py-2 bg-[#1C1917] text-white text-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50" data-testid="save-settings">{saving ? 'Saving...' : 'Save Settings'}</button>
    </div>
  );
}

function TabBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', published: false });
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');

  useEffect(() => { axios.get(`${API}/blogs?published_only=false`, getAuth()).then(r => setBlogs(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (editing) { await axios.put(`${API}/admin/blogs/${editing}`, form, getAuth()); }
    else { await axios.post(`${API}/admin/blogs`, form, getAuth()); }
    const r = await axios.get(`${API}/blogs?published_only=false`, getAuth());
    setBlogs(r.data); setForm({ title: '', slug: '', excerpt: '', content: '', published: false }); setEditing(null);
  };
  const del = async (id) => { await axios.delete(`${API}/admin/blogs/${id}`, getAuth()); setBlogs(blogs.filter(b => b.id !== id)); };
  const edit = (b) => { setForm({ title: b.title, slug: b.slug, excerpt: b.excerpt || '', content: b.content, published: b.published }); setEditing(b.id); };

  const generateBlogs = async () => {
    setGenerating(true); setGenMsg('Generating 8 blog posts with AI... This takes about 60 seconds.');
    try {
      const r = await axios.post(`${API}/admin/generate-blogs`, {}, getAuth());
      setGenMsg(`Done! ${r.data.generated} new posts generated.`);
      const br = await axios.get(`${API}/blogs?published_only=false`, getAuth());
      setBlogs(br.data);
    } catch (e) { setGenMsg('Error generating blogs. Check API key.'); }
    setGenerating(false);
  };

  return (
    <div className="space-y-4">
      {/* AI Generation */}
      <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4">
        <h3 className="font-semibold text-[#1C1917] mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-[#C5A028]" /> AI Blog Generator</h3>
        <p className="text-xs text-[#78716C] mb-3">Generate 8 SEO-optimized blog posts about clothing sizing using AI. Posts are auto-published.</p>
        <button onClick={generateBlogs} disabled={generating} className="px-4 py-2 bg-[#C5A028] text-white text-sm font-medium hover:bg-[#1C1917] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="generate-blogs-btn">
          <Zap className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate 8 Blog Posts'}
        </button>
        {genMsg && <p className={`text-sm mt-2 ${genMsg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{genMsg}</p>}
      </div>

      {/* Manual Blog Form */}
      <h3 className="font-semibold text-[#1C1917]">{editing ? 'Edit Blog Post' : 'Manual Blog Post'}</h3>
      <div className="grid grid-cols-2 gap-3">
        <input value={form.title} onChange={e => setForm({...form, title: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')})} placeholder="Title" className="p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" data-testid="blog-title-input" />
        <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="slug" className="p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" />
      </div>
      <input value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} placeholder="Excerpt" className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" />
      <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Content (Markdown)" rows={6} className="w-full p-2 border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" />
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => setForm({...form, published: e.target.checked})} /> Published</label>
        <button onClick={save} className="px-4 py-2 bg-[#1C1917] text-white text-sm hover:bg-[#C5A028]" data-testid="blog-save">{editing ? 'Update' : 'Create'}</button>
        {editing && <button onClick={() => { setEditing(null); setForm({ title: '', slug: '', excerpt: '', content: '', published: false }); }} className="text-sm text-[#78716C]">Cancel</button>}
      </div>

      {/* Blog List */}
      <div className="mt-4 space-y-2">
        <p className="text-sm text-[#78716C] font-medium">{blogs.length} blog posts total</p>
        {blogs.map(b => (
          <div key={b.id} className="flex items-center justify-between p-3 bg-[#FAFAFA] border border-[#E8E2D9]">
            <div className="flex-1 min-w-0 mr-3"><p className="text-sm font-medium text-[#1C1917] truncate">{b.title}</p><p className="text-xs text-[#78716C]">{b.published ? 'Published' : 'Draft'}</p></div>
            <div className="flex gap-2 flex-shrink-0"><button onClick={() => edit(b)} className="p-1 hover:text-[#C5A028]"><Edit2 className="w-4 h-4" /></button><button onClick={() => del(b.id)} className="p-1 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabBrandsWave() {
  const [brands, setBrands] = useState([]);
  const [pageCount, setPageCount] = useState({});
  const [loading, setLoading] = useState('');
  const [msg, setMsg] = useState('');

  const refresh = async () => {
    const br = await axios.get(`${API}/admin/brands`, getAuth()).catch(() => ({ data: [] }));
    setBrands(br.data);
    const pc = await axios.get(`${API}/page-count`).catch(() => ({ data: {} }));
    setPageCount(pc.data);
  };
  useEffect(() => { refresh(); }, []);

  const reseed = async (label) => {
    setLoading(label); setMsg('');
    try {
      const r = await axios.post(`${API}/admin/reseed-brands`, {}, getAuth());
      setMsg(`${label}: ${r.data.message}`);
      await refresh();
    } catch { setMsg('Error reseeding'); }
    setLoading('');
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-[#1C1917]">Brands & Scaling</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-center"><p className="text-xl font-bold text-[#C5A028]">{pageCount.total_pages || 0}</p><p className="text-[10px] text-[#78716C]">Total Pages</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-center"><p className="text-xl font-bold text-[#C5A028]">{pageCount.brand_pages || 0}</p><p className="text-[10px] text-[#78716C]">Brands</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-center"><p className="text-xl font-bold text-[#C5A028]">{pageCount.brand_category_pages || 0}</p><p className="text-[10px] text-[#78716C]">Brand+Cat</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-center"><p className="text-xl font-bold text-[#C5A028]">{pageCount.conversion_pages || 0}</p><p className="text-[10px] text-[#78716C]">Conversions</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-center"><p className="text-xl font-bold text-[#C5A028]">{pageCount.brand_conversion_pages || 0}</p><p className="text-[10px] text-[#78716C]">Brand Conv</p></div>
      </div>

      {/* Wave Buttons */}
      <div className="bg-white border border-[#E8E2D9] p-4">
        <h4 className="font-semibold text-sm text-[#1C1917] mb-3">Deploy Brand Waves</h4>
        <p className="text-xs text-[#78716C] mb-3">Click a wave to load new brands. Each wave adds ~50 brands + hundreds of new pages to the sitemap.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => reseed('Wave 1+2')} disabled={!!loading} className="px-4 py-2 bg-[#1C1917] text-white text-sm font-medium hover:bg-[#C5A028] transition-colors disabled:opacity-50" data-testid="wave-1-2">
            {loading === 'Wave 1+2' ? 'Loading...' : 'Wave 1+2 (100 brands)'}
          </button>
          <button onClick={() => reseed('Wave 3')} disabled={!!loading} className="px-4 py-2 bg-[#C5A028] text-white text-sm font-medium hover:bg-[#1C1917] transition-colors disabled:opacity-50" data-testid="wave-3">
            {loading === 'Wave 3' ? 'Loading...' : 'Wave 3 (+25 brands)'}
          </button>
          <button onClick={() => reseed('Wave 4')} disabled={!!loading} className="px-4 py-2 bg-[#C5A028] text-white text-sm font-medium hover:bg-[#1C1917] transition-colors disabled:opacity-50" data-testid="wave-4">
            {loading === 'Wave 4' ? 'Loading...' : 'Wave 4 (+25 brands)'}
          </button>
          <button onClick={() => reseed('All Waves')} disabled={!!loading} className="px-4 py-2 border-2 border-[#C5A028] text-[#C5A028] text-sm font-bold hover:bg-[#C5A028] hover:text-white transition-colors disabled:opacity-50" data-testid="wave-all">
            {loading === 'All Waves' ? 'Loading...' : 'Deploy ALL Waves'}
          </button>
        </div>
        {msg && <p className="text-sm text-green-600 mt-3">{msg}</p>}
      </div>

      <div className="max-h-48 overflow-y-auto space-y-1">
        {brands.map(b => (
          <div key={b.slug} className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E8E2D9] text-xs">
            <span className="font-medium">{b.name} <span className="text-[#78716C]">({b.country_origin}) {b.categories?.length || 0} cats</span></span>
            {b.popular && <span className="text-[#C5A028] text-[10px] font-bold">POPULAR</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function TabIndexNow() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [urlCount, setUrlCount] = useState(0);
  useEffect(() => { axios.get(`${API}/page-count`).then(r => setUrlCount(r.data.total_pages || 0)).catch(() => {}); }, []);
  const submit = async () => {
    setLoading(true);
    try { const r = await axios.post(`${API}/admin/indexnow`, {}, getAuth()); setResult(r.data); }
    catch { setResult({ error: 'Failed to submit' }); }
    setLoading(false);
  };
  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-[#1C1917]">IndexNow — Submit to Bing, Yahoo, Yandex, DuckDuckGo</h3>
      <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4">
        <p className="text-sm mb-1"><strong>Endpoint:</strong> https://www.bing.com/indexnow</p>
        <p className="text-sm mb-1"><strong>URLs to submit:</strong> {urlCount}</p>
        <p className="text-xs text-[#78716C] mb-3">Submits in batches of 500 (max per request). One click indexes everything.</p>
        <button onClick={submit} disabled={loading} className="px-6 py-2 bg-[#1C1917] text-white text-sm font-medium hover:bg-[#C5A028] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="indexnow-submit">
          <Send className="w-4 h-4" /> {loading ? 'Submitting...' : `Submit ${urlCount} URLs to IndexNow`}
        </button>
      </div>
      {result && !result.error && <div className="bg-green-50 border border-green-200 p-4 text-sm text-green-700">Submitted {result.submitted || 0} URLs in {result.batches} batch(es)!</div>}
      {result?.error && <div className="bg-red-50 border border-red-200 p-4 text-sm text-red-600">{result.error}</div>}
    </div>
  );
}

function TabStats() {
  const [stats, setStats] = useState({});
  const [pageCount, setPageCount] = useState({});
  useEffect(() => { axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {}); axios.get(`${API}/page-count`).then(r => setPageCount(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h3 className="font-semibold text-[#1C1917] mb-4">Site Statistics</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 text-center"><p className="text-2xl font-bold text-[#C5A028]">{stats.sessions?.toLocaleString() || 0}</p><p className="text-xs text-[#78716C]">Sessions</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 text-center"><p className="text-2xl font-bold text-[#C5A028]">{pageCount.total_pages || 0}</p><p className="text-xs text-[#78716C]">Total Pages</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 text-center"><p className="text-2xl font-bold text-[#C5A028]">{pageCount.brand_pages || 0}</p><p className="text-xs text-[#78716C]">Brands</p></div>
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 text-center"><p className="text-2xl font-bold text-[#C5A028]">{pageCount.blog_pages || 0}</p><p className="text-xs text-[#78716C]">Blog Posts</p></div>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'settings', label: 'Settings', icon: Settings },
  { key: 'blogs', label: 'Blog', icon: BookOpen },
  { key: 'brands', label: 'Brands & Scale', icon: Layers },
  { key: 'indexnow', label: 'IndexNow', icon: Send },
  { key: 'stats', label: 'Analytics', icon: BarChart3 },
];

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState('settings');
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1C1917]">Admin Dashboard</h1>
        <button onClick={onLogout} className="flex items-center gap-2 text-sm text-[#78716C] hover:text-red-500" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-colors ${tab === t.key ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid={`admin-tab-${t.key}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white border border-[#E8E2D9] p-6">
        {tab === 'settings' && <TabSettings />}
        {tab === 'blogs' && <TabBlogs />}
        {tab === 'brands' && <TabBrandsWave />}
        {tab === 'indexnow' && <TabIndexNow />}
        {tab === 'stats' && <TabStats />}
      </div>
    </div>
  );
}
